import React from "react";

import SelectOneOptionDropdownQuestion from "components/pages/Questionnaire/components/SelectOneOptionDropdownQuestion/SelectOneOptionDropdownQuestion";
import { QuestionType } from "enums/questionnaire";
import { IUser } from "types/auth";
import { TaxProfile } from "types/client";
import { checkDepends, formatAnswers } from "utils/new-questionnaire";

import { Question } from "../../constants/questions";
import AddressQuestion from "../AddressQuestion/AddressQuestion";
import AmountQuestion from "../AmountQuestion/AmountQuestion";
import DateQuestion from "../DateQuestion/DateQuestion";
import SelectManyOptionQuestion from "../SelectManyOptionQuestion/SelectManyOptionQuestion";
import SelectOneOptionQuestion from "../SelectOneOptionQuestion/SelectOneOptionQuestion";
import SelectOneOptionQuestionV2 from "../SelectOneOptionQuestionV2/SelectOneOptionQuestionV2";
import TextQuestion from "../TextQuestion/TextQuestion";
import YesNoQuestion from "../YesNoQuestion/YesNoQuestion";

interface ConditionalQuestionsRendererProps {
    data: Question;
    currentProfileAnswers: {
        question: string;
        answer: any;
        type: string;
        id: string;
    }[];
    currentProfile?: TaxProfile;
    onAnswer: (_question: string, _answer: any, _questionType?: string) => void;
    handlePrev: () => void;
    profileName: string;
    setContinueDisabled?: (_disabled: boolean) => void;
    user: IUser
}

const ConditionalQuestionsRenderer: React.FC<ConditionalQuestionsRendererProps> = ({
    data,
    currentProfileAnswers,
    currentProfile,
    onAnswer,
    handlePrev,
    profileName,
    setContinueDisabled,
    user,
}) => {
    const formattedAnswers = formatAnswers(currentProfileAnswers);

    return (
        <div className="new-questionnaire__question">
            {data.questionTitle && (
                <div className="new-questionnaire__list-question-title" >{data.questionTitle}</div>
            )}
            {data?.questions?.map(question => {
                if (question.depend?.length && !checkDepends(question.depend, formattedAnswers)) {
                    return null;
                }

                const initialValue = currentProfileAnswers.find(q => q.question === question.question)?.answer;

                switch (question.type) {
                case QuestionType.yesNoQuestion:
                    return (
                        <YesNoQuestion
                            key={question.id}
                            data={question}
                            onAnswer={onAnswer}
                            initialValue={initialValue}
                            insertItemName={!!question.itemNameFromQuestionAnswer}
                            itemName={profileName}
                        />
                    );
                case QuestionType.dateQuestion:
                    return (
                        <DateQuestion
                            key={question.id}
                            data={question}
                            onAnswer={onAnswer}
                            initialValue={initialValue}
                        />
                    );
                case QuestionType.amountQuestion:
                    return (
                        <AmountQuestion
                            key={question.id}
                            data={question}
                            handlePrev={handlePrev}
                            onAnswer={onAnswer}
                            initialValue={initialValue}
                            arrayAnswers={currentProfileAnswers}
                            taxProfile={currentProfile}
                        />
                    );
                case QuestionType.textQuestion:
                    return (
                        <TextQuestion
                            key={question.id}
                            data={question}
                            onAnswer={onAnswer}
                            initialValue={initialValue}
                        />
                    );
                case QuestionType.selectManyQuestion:
                    return (
                        <SelectManyOptionQuestion
                            key={question.id}
                            data={question}
                            onAnswer={onAnswer}
                            categoryAnswers={formatAnswers(currentProfileAnswers)}
                            initialValue={initialValue}
                            handlePrev={handlePrev}

                        />
                    );
                case QuestionType.addressQuestion:
                    return (
                        <AddressQuestion
                            key={question.id}
                            data={question}
                            onAnswer={onAnswer}
                            initialValue={initialValue}
                            setContinueDisabled={setContinueDisabled!}
                            nonUKCountryEnabled={true}
                        />
                    );
                case QuestionType.selectOneOptionQuestion:
                    return (
                        <SelectOneOptionQuestion
                            key={question.id}
                            user={user!}
                            data={question}
                            onAnswer={onAnswer}
                            initialValue={currentProfileAnswers.find(q => q.question === question.question)?.answer}
                        />
                    );

                case QuestionType.selectOneOptionDropdownQuestion:
                    return (
                        <SelectOneOptionDropdownQuestion
                            key={question.id}
                            user={user!}
                            data={question}
                            onAnswer={onAnswer}
                            initialValue={currentProfileAnswers.find(q => q.question === question.question)?.answer}
                        />
                    );

                case QuestionType.selectOneOptionQuestionV2:
                    return (
                        <SelectOneOptionQuestionV2
                            key={question.id}
                            data={question}
                            onAnswer={onAnswer}
                            initialValue={currentProfileAnswers.find(q => q.question === question.question)?.answer}
                            handlePrev={handlePrev}
                            arrayAnswers={currentProfileAnswers}
                        />
                    );
                default:
                    return null;
                }
            })}
        </div>
    );
};

export default ConditionalQuestionsRenderer;
